import { useCallback, useEffect, useMemo, useState } from 'react';
import { orderApi } from '@formosa/shared/api/order';
import { normalizeSymbolCode } from '@formosa/shared/utils/marketThumb';
import {
  formatFundAmount,
  formatSignedFundAmount,
  getAmountTone,
  tf,
} from '../../constants/fundConstants';
import {
  collectPerpetualSymbolCodes,
  sumNumericField,
  sumPerpetualUnrealizedPnl,
  sumTodayProfit,
} from '../../constants/positionConstants';
import { useAuth } from '../../auth/AuthContext';
import { useTranslation } from '../../i18n/LocaleContext';
import useMarketThumbSocket from '../../hooks/useMarketThumbSocket';
import WalletPerpetualOpenItem from './WalletPerpetualOpenItem';
import WalletPositionHistoryItem from './WalletPositionHistoryItem';

const POSITION_TABS = [
  { key: 'timed', labelKey: 'funds.tabTimed' },
  { key: 'futures', labelKey: 'funds.tabFutures' },
  { key: 'perpetual', labelKey: 'funds.tabPerpetual' },
];

const TAB_STATS_CONFIG = {
  timed: {
    primaryLabelKey: 'funds.todayProfit',
    secondaryLabelKey: 'funds.positionMargin',
    listHeadingKey: 'funds.historyPositions',
    primarySigned: true,
    secondarySigned: false,
  },
  futures: {
    primaryLabelKey: 'funds.todayProfit',
    secondaryLabelKey: 'funds.futuresHolding',
    listHeadingKey: 'funds.historyPositions',
    primarySigned: true,
    secondarySigned: false,
  },
  perpetual: {
    primaryLabelKey: 'funds.marginBalance',
    secondaryLabelKey: 'funds.unrealizedPnl',
    listHeadingKey: 'funds.openPositions',
    primarySigned: false,
    secondarySigned: true,
  },
};

const PAGE_SIZE = 15;

async function fetchTimedPositionData(pageNo) {
  const [activePage, historyPage, todayPage] = await Promise.all([
    orderApi.listTimedOrders({ pageNo: 1, pageSize: 200, status: 'START,PROCESS' }),
    orderApi.listTimedOrders({ pageNo, pageSize: PAGE_SIZE, status: 'END,FAIL' }),
    orderApi.listTimedOrders({ pageNo: 1, pageSize: 200, status: 'END' }),
  ]);

  return {
    primaryValue: sumTodayProfit(todayPage?.records, 'createTime'),
    secondaryValue: sumNumericField(activePage?.records, 'betAmount'),
    records: historyPage?.records || [],
    historyPage,
  };
}

async function fetchFuturesPositionData(pageNo) {
  const [activePage, historyPage, todayPage] = await Promise.all([
    orderApi.listFuturesOrders({ pageNo: 1, pageSize: 200, state: 'BOOKED' }),
    orderApi.listFuturesOrders({ pageNo, pageSize: PAGE_SIZE, state: 'SETTLED,CANCELLED' }),
    orderApi.listFuturesOrders({ pageNo: 1, pageSize: 200, state: 'SETTLED' }),
  ]);

  return {
    primaryValue: sumTodayProfit(todayPage?.records, 'settleTime'),
    secondaryValue: sumNumericField(activePage?.records, 'orderAmount'),
    records: historyPage?.records || [],
    historyPage,
  };
}

async function fetchPerpetualPositionData(walletBalance) {
  const openPage = await orderApi.listPerpetualOrders({ pageNo: 1, pageSize: 200, status: 'OPEN' });
  const openPositions = openPage?.records || [];

  return {
    primaryValue: Number(walletBalance) || 0,
    openPositions,
    records: openPositions,
    historyPage: { current: 1, pages: 1 },
  };
}

const TAB_LOADERS = {
  timed: fetchTimedPositionData,
  futures: fetchFuturesPositionData,
  perpetual: fetchPerpetualPositionData,
};

export default function WalletPositionsSection({
  coinName = 'USDT',
  balanceVisible = true,
  walletBalance = 0,
}) {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('timed');
  const [primaryValue, setPrimaryValue] = useState(0);
  const [secondaryValue, setSecondaryValue] = useState(0);
  const [openPositions, setOpenPositions] = useState([]);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  const statsConfig = TAB_STATS_CONFIG[activeTab];
  const perpetualSymbols = useMemo(
    () => (activeTab === 'perpetual' ? collectPerpetualSymbolCodes(openPositions) : []),
    [activeTab, openPositions],
  );
  const thumbs = useMarketThumbSocket(perpetualSymbols);

  const unrealizedPnl = useMemo(() => {
    if (activeTab !== 'perpetual') {
      return secondaryValue;
    }
    return sumPerpetualUnrealizedPnl(openPositions, thumbs);
  }, [activeTab, openPositions, secondaryValue, thumbs]);

  const displayedSecondary = activeTab === 'perpetual' ? unrealizedPnl : secondaryValue;
  const primaryTone = statsConfig.primarySigned ? getAmountTone(primaryValue) : 'neutral';
  const secondaryTone = statsConfig.secondarySigned ? getAmountTone(displayedSecondary) : 'neutral';

  const loadTabData = useCallback(
    async (tab, pageNo, append = false) => {
      if (!isAuthenticated) {
        setPrimaryValue(0);
        setSecondaryValue(0);
        setOpenPositions([]);
        setRecords([]);
        setPage(1);
        setTotalPages(1);
        setError('');
        return;
      }

      if (pageNo === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setError('');

      try {
        const loader = TAB_LOADERS[tab];
        const result = tab === 'perpetual'
          ? await loader(walletBalance)
          : await loader(pageNo);

        if (pageNo === 1) {
          setPrimaryValue(result.primaryValue ?? 0);
          setSecondaryValue(result.secondaryValue ?? 0);
          if (tab === 'perpetual') {
            setOpenPositions(result.openPositions || []);
          } else {
            setOpenPositions([]);
          }
        }

        const rows = result.records || [];
        setRecords((prev) => (append ? [...prev, ...rows] : rows));
        setPage(result.historyPage?.current || pageNo);
        setTotalPages(result.historyPage?.pages || 1);
      } catch (err) {
        if (!append) {
          setPrimaryValue(0);
          setSecondaryValue(0);
          setOpenPositions([]);
          setRecords([]);
        }
        setError(err.message || t('funds.positionLoadFailed'));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [isAuthenticated, t, walletBalance],
  );

  useEffect(() => {
    loadTabData(activeTab, 1, false);
  }, [activeTab, loadTabData]);

  function handleTabChange(tab) {
    if (tab === activeTab) {
      return;
    }
    setActiveTab(tab);
    setRecords([]);
    setOpenPositions([]);
    setPage(1);
    setTotalPages(1);
  }

  function handleLoadMore() {
    if (activeTab === 'perpetual' || loadingMore || page >= totalPages) {
      return;
    }
    loadTabData(activeTab, page + 1, true);
  }

  function maskAmount(value) {
    return balanceVisible ? formatFundAmount(value) : '******';
  }

  function maskSignedAmount(value) {
    return balanceVisible ? formatSignedFundAmount(value) : '******';
  }

  function formatStatValue(value, signed) {
    return signed ? maskSignedAmount(value) : maskAmount(value);
  }

  return (
    <section className="funds-positions">
      <div className="funds-page__tabs funds-positions__tabs" role="tablist" aria-label={t('funds.positionsTitle')}>
        {POSITION_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={`funds-page__tab ${activeTab === tab.key ? 'is-active' : ''}`}
            onClick={() => handleTabChange(tab.key)}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      <div className="funds-positions__stats">
        <div>
          <span>{tf(t, statsConfig.primaryLabelKey, { coin: coinName })}</span>
          <strong className={`funds-positions__value is-${primaryTone}`}>
            {formatStatValue(primaryValue, statsConfig.primarySigned)}
          </strong>
        </div>
        <div>
          <span>
            {statsConfig.secondaryLabelKey === 'funds.futuresHolding'
              ? t(statsConfig.secondaryLabelKey)
              : tf(t, statsConfig.secondaryLabelKey, { coin: coinName })}
          </span>
          <strong className={`funds-positions__value is-${secondaryTone}`}>
            {formatStatValue(displayedSecondary, statsConfig.secondarySigned)}
          </strong>
        </div>
      </div>

      <h2 className="funds-positions__heading">{t(statsConfig.listHeadingKey)}</h2>

      {!isAuthenticated && (
        <div className="funds-page__empty">
          <div className="funds-page__empty-icon" aria-hidden>
            📄
          </div>
          <p>{t('funds.positionLoginHint')}</p>
        </div>
      )}

      {isAuthenticated && loading && <p className="funds-page__status">{t('common.loading')}</p>}
      {isAuthenticated && error && <p className="funds-page__error">{error}</p>}

      {isAuthenticated && !loading && !error && records.length === 0 && (
        <div className="funds-page__empty">
          <div className="funds-page__empty-icon" aria-hidden>
            📄
          </div>
          <p>{t('common.noData')}</p>
        </div>
      )}

      {isAuthenticated && !loading && records.length > 0 && (
        <>
          <ul className="funds-position-list">
            {activeTab === 'perpetual'
              ? openPositions.map((item) => (
                  <WalletPerpetualOpenItem
                    key={item.id || item.orderSerialId}
                    order={item}
                    thumb={thumbs[normalizeSymbolCode(item.marketSymbolCode || '')]}
                    t={t}
                  />
                ))
              : records.map((item) => (
                  <WalletPositionHistoryItem
                    key={item.id || item.orderSerialId}
                    order={item}
                    tab={activeTab}
                    t={t}
                  />
                ))}
          </ul>

          {activeTab !== 'perpetual' && page < totalPages && (
            <div className="funds-page__footer">
              <button
                type="button"
                className="funds-page__load-more"
                disabled={loadingMore}
                onClick={handleLoadMore}
              >
                {loadingMore ? t('common.loading') : t('funds.loadMore')}
              </button>
            </div>
          )}

          {(activeTab === 'perpetual' || page >= totalPages) && records.length > 0 && (
            <p className="funds-page__footer">{t('funds.noMore')}</p>
          )}
        </>
      )}
    </section>
  );
}
